import { CommonModule } from '@angular/common'
import {
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  EventEmitter,
  input,
  OnInit,
  Output,
  signal,
  ViewEncapsulation,
} from '@angular/core'
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms'
import { MatDialogModule, MatDialogRef } from '@angular/material/dialog'
import { MatDividerModule } from '@angular/material/divider'
import { MatFormFieldModule } from '@angular/material/form-field'
import { MatIconModule } from '@angular/material/icon'
import { MatInputModule } from '@angular/material/input'
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner'
import { MatSelectModule } from '@angular/material/select'
import { MatSnackBar } from '@angular/material/snack-bar'
import { EditWordPairDicCommand } from 'src/app/core/commands/edit-word-pair-dic/edit-word-pair-dic.command'
import { AngularMaterialsModules } from 'src/app/module/materials.module'
import { CategoryService } from 'src/app/service/data/category.service'
import { DictionaryService } from 'src/app/service/data/dictionary.service'
import { TagService } from 'src/app/service/data/tag.service'
import { ThemeService } from 'src/app/service/theme/theme.service'
import { Article, ArticlesByLanguage, Languages, PartOfSpeech } from '../../enums/enums'
import { SelectOption } from '../../interfaces/word'
import { Category } from '../../models/main/category'
import { Tag } from '../../models/main/tag'
import { Word } from '../../models/main/word'
import { DdcAddMobileSheetComponent } from '../ddc-add-mobile-sheet/ddc-add-mobile-sheet.component'
import { DdcButtonSOneComponent } from '../ddc-button-s-one/ddc-button-s-one.component'
import { DdcTextGradientComponent } from '../ddc-text-gradient/ddc-text-gradient.component'

@Component({
  selector: 'ddc-filter-mobile-sheet',
  templateUrl: './ddc-filter-mobile-sheet.component.html',
  styleUrl: './ddc-filter-mobile-sheet.component.scss',
  standalone: true,
  encapsulation: ViewEncapsulation.None,
  imports: [
    AngularMaterialsModules,
    CommonModule,
    MatIconModule,
    FormsModule,
    MatDividerModule,
    DdcTextGradientComponent,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatProgressSpinnerModule,
    MatSelectModule,
    ReactiveFormsModule,
    MatDialogModule,
    DdcButtonSOneComponent,
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class DdcFilterMobileSheetComponent implements OnInit {
  type = input<'words' | 'pairs'>()
  @Output() edit = new EventEmitter<Word>()
  @Output() cancel = new EventEmitter<void>()

  protected EditWordPairDicCommand: EditWordPairDicCommand
  _form: FormGroup

  private formValues = signal<any>(null)
  protected selectedTags = signal<Tag[]>([])
  protected selectedTags2 = signal<Tag[]>([])
  protected selectedCategory = signal<Category[]>([])
  protected selectedCategory2 = signal<Category[]>([])
  protected selectedDics = signal<{ id: number; label: string }[]>([])

  _isDialogLoading = false
  _isDarkTheme = false
  _isLightTheme = true
  _isPaperTheme = false
  _wordCreated = false
  _pairCreated = false
  _hasDic = false
  _targetDicTitle = ''
  _addError = ''
  _isSubscriptionError = false
  _addedField = ''
  _addedField2 = ''

  _selectedLanguage = ''
  _selectedLanguage2 = ''

  _selectedArticle = ''
  _selectedArticle2 = ''

  _selectedPos = ''
  _selectedPos2 = ''

  _selectedLngHasArticle = false
  _selectedLngHasArticle2 = false

  _wordMaxlength = 100
  _definitionMaxlength = 500
  _exampleMaxlength = 200

  _loadedCats: boolean = false
  _loadedTags: boolean = false
  _loadedDics: boolean = false

  _userCats: any[] = []
  _userTags: any[] = []
  _userDics: any[] = []
  _isCreateBtnLoading = false
  _submitBtnText = 'Filter'
  _selectedPrivacyChipOptions: string = ''
  _selectedChipOptions: string[] = ['word']

  languages: SelectOption[] = []
  articles: SelectOption[] = []
  poses: SelectOption[] = []

  languages2: SelectOption[] = []
  articles2: SelectOption[] = []
  poses2: SelectOption[] = []

  constructor(
    private _fb: FormBuilder,
    private _themeService: ThemeService,
    private _snackBar: MatSnackBar,
    private cat_service: CategoryService,
    private tag_service: TagService,
    private dic_service: DictionaryService,
    private _dialogRef: MatDialogRef<DdcAddMobileSheetComponent>,
    editWordPairDicCmd: EditWordPairDicCommand,
  ) {
    this.EditWordPairDicCommand = editWordPairDicCmd
    this._form = this._fb.group({
      _type: ['filter'],

      _1_language: [''],
      _2_language: [''],

      _1_article: [''],
      _1_pos: [''],
    })

    this._themeService.theme$.subscribe(theme => {
      this._isDarkTheme = theme === 'dark-theme'
      this._isLightTheme = theme === 'light-theme'
      this._isPaperTheme = theme === 'paper-theme'
    })
  }

  ngOnInit(): void {
    this._selectedPrivacyChipOptions = 'P01'
    this.getLanguages()
    this.getArticles()
    this.getPos()
    this.getUserTags()
    this.getUserCats()
    this.getUserDics()

    // Initialize form values signal
    this.formValues.set(this._form.value)

    Promise.all([this.getLanguages(), this.getArticles(), this.getPos()]).then(() => {
      setTimeout(() => {
        this._isDialogLoading = false
      }, 500)
    })
  }

  getLanguages(): void {
    this.languages = Object.entries(Languages).map(([key, [viewValue, rawValue]]) => ({
      raw_value: rawValue,
      view_value: viewValue,
    }))
  }

  getArticles(): void {
    const articlesArray = Object.values(Article) as string[]

    this.articles.push({
      raw_value: null,
      view_value: 'None',
    })

    // Loop through the enum values and create SelectOption objects
    for (const value of articlesArray) {
      this.articles.push({
        raw_value: value,
        view_value: value,
      })
    }
  }

  getUserTags(): void {
    this.tag_service.get_tags().subscribe((res: any) => {
      if (res) {
        this._userTags = res
        this._loadedTags = true
      }
    })
  }

  getUserCats(): void {
    this.cat_service.get_cats().subscribe((res: any) => {
      if (res) {
        this._userCats = res
        this._loadedCats = true
      }
    })
  }

  getUserDics(): void {
    this.dic_service.get_dics(true).subscribe({
      next: (res: any) => {
        if (res) {
          // Map the response to transform 'title' to 'label'
          this._userDics = res.map((dic: { id: number; title: string }) => ({
            id: dic.id,
            label: dic.title,
          }))
          this._loadedDics = true
        } else {
        }
      },
      error: (error: any) => {
        this._isSubscriptionError = true
      },
    })
  }

  getPos(): void {
    const posArray = Object.values(PartOfSpeech) as string[]

    this.poses.push({
      raw_value: null,
      view_value: 'None',
    })

    // Loop through the enum values and create SelectOption objects
    for (const value of posArray) {
      this.poses.push({
        raw_value: value,
        view_value: value,
      })
    }
  }

  onLanguageChange(event: any): void {
    const selectedLng = event.value

    this.articles = []
    this.articles.push({
      raw_value: null,
      view_value: 'None',
    })

    const articlesArray = ArticlesByLanguage[selectedLng]

    if (articlesArray !== undefined && articlesArray.length > 0) {
      this._selectedLngHasArticle = true
      for (const value of articlesArray) {
        this.articles.push({
          raw_value: value,
          view_value: value,
        })
      }
    } else {
      this._selectedLngHasArticle = false
      this._selectedArticle = null
    }
  }

  onLanguageChange2(event: any): void {
    const selectedLng = event.value

    this.articles = []
    this.articles.push({
      raw_value: null,
      view_value: 'None',
    })

    const articlesArray = ArticlesByLanguage[selectedLng]

    if (articlesArray !== undefined && articlesArray.length > 0) {
      this._selectedLngHasArticle = true
      for (const value of articlesArray) {
        this.articles.push({
          raw_value: value,
          view_value: value,
        })
      }
    } else {
      this._selectedLngHasArticle = false
      this._selectedArticle = null
    }
  }

  updateSelectedTags(tags: any): void {
    this.selectedTags.set(tags)
  }

  updateSelectedCat(cat: any): void {
    this.selectedCategory.set(cat)
  }

  updateSelectedDics(dictionaries: any): void {
    this.selectedDics.set(dictionaries)
  }

  add(): void {
    if (this._form.valid) {
    } else {
      this._snackBar.open('Please fill in all required fields', 'Close', {
        duration: 3000,
      })
    }
  }

  closeDialog(): void {
    this._dialogRef.close()
  }
}
